type TestId = string;

import { ComponentType, createElement, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonOwnPropsBase {
    /** HTML id attribute. Prefer testId for automation selectors. */
    id?: string;
    /** Stable DOM selector for automation. */
    'data-id'?: string;
    /** Stable automation selector. Prefer centralized values from *TestIds files. */
    testId?: TestId;
    /** How large should the button be? */
    size?: 'small' | 'medium' | 'large';
    /** Button variant */
    variant?: 'primary' | 'secondary' | 'black' | 'ghost' | 'outlined' | 'error';
    /** Is button disabled */
    disabled?: boolean;
    /** Shows an animated spinner (replaces icon, implies disabled). */
    loading?: boolean;
    /** Optional click handler */
    onClick?: () => void;
    className?: string;
    iconPosition?: 'left' | 'right';
    defaultPadding?: boolean;
    rounded?: 'full' | 'left' | 'right' | 'none';
}

// At least one of label or icon must be provided to avoid an empty button.
type ButtonOwnProps = ButtonOwnPropsBase & (
    | { label: string; icon?: ComponentType<React.SVGProps<SVGSVGElement>> }
    | { label?: string; icon: ComponentType<React.SVGProps<SVGSVGElement>> }
);

// Merge with HTML button attributes so autoFocus, aria-*, title etc. pass through.
// onClick is omitted because the custom signature (() => void) differs from MouseEventHandler.
export type ButtonProps = ButtonOwnProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOwnProps | 'onClick' | 'children'>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
    size = 'medium',
    variant = 'primary',
    disabled = false,
    loading = false,
    label,
    icon,
    className,
    iconPosition = 'left',
    defaultPadding = true,
    rounded = 'full',
    onClick,
    testId,
    ...props
}, ref) => {
    const sizeConfig = {
        small: {
            style: 'px-3 py-1.5 text-xs',
            iconClass: 'w-3 h-3',
        },
        medium: {
            style: `${defaultPadding ? 'px-4' : 'px-0'} py-[0.375rem] text-sm`,
            iconClass: 'w-4 h-4',
        },
        large: {
            style: 'px-5 py-2.5 text-sm',
            iconClass: 'w-5 h-5',
        },
    };

    // Variant styles
    const variantStyles = {
        primary: 'bg-accent text-white dark:text-[#fff]',
        secondary: 'bg-background-light border-border border text-primary hover:bg-border',
        black: 'bg-primary text-white',
        ghost: 'bg-transparent text-primary hover:bg-border',
        outlined: 'bg-transparent border-border border text-secondary hover:border-secondary hover:bg-border/10',
        error: 'bg-white border-error border text-error hover:bg-error/5',
    };

    const isDisabled = disabled || loading;

    // Primary variant hover effect
    const primaryHoverClass =
        variant === 'primary' && !isDisabled
            ? [
                'transition-shadow duration-150 ease-in',
                'hover:shadow-[inset_0_2px_24px_0_rgba(0,0,0,0.25)]',
                'before:absolute before:inset-0',
                'before:shadow-[inset_0_4px_16px_0_#FFF]',
                'before:mix-blend-overlay before:pointer-events-none',
                'after:absolute after:inset-0',
                'after:shadow-[inset_0_-4px_16px_0_rgba(0,0,0,0.80)]',
                'after:mix-blend-overlay after:pointer-events-none',
            ].join(' ')
            : '';

    // Icon-only button dimensions.
    // rounded-full: fixed 32×28px pill. Split variants (left/right): no fixed
    // size — vertical padding is calculated to match the labeled-button height
    // (py-2 + h-4 icon = 8+16+8 = 32px, same as py-[0.375rem] + text-sm).
    const iconOnlyStyle = !label && rounded === 'full'
        ? {
            width: '2rem', // 32px
            height: '1.75rem', // 28px
            padding: '0',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        }
        : {};

    const roundedClass = {
        full: 'rounded-full',
        left: 'rounded-l-full rounded-r-none',
        right: 'rounded-l-none rounded-r-full',
        none: 'rounded-none',
    }[rounded]

    const buttonClasses = [
        'inline-flex shrink-0 items-center justify-center font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
        roundedClass,
        label ? 'gap-[.375rem]' : '',
        label ? sizeConfig[size].style : (rounded !== 'full' ? 'px-2 py-2' : ''),
        variantStyles[variant],
        isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        primaryHoverClass,
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const effectiveIcon = loading ? Loader2 : icon;
    const iconSizeClass = !label ? 'w-4 h-4' : sizeConfig[size].iconClass;
    const iconClass = `shrink-0 ${iconSizeClass} icon-stroke-thick${loading ? ' animate-spin' : ''}`;

    const iconNode = effectiveIcon
        ? createElement(effectiveIcon, { className: iconClass })
        : null;

    return (
        <button
            ref={ref}
            type="button"
            className={buttonClasses}
            style={{
                position: 'relative',
                overflow: 'hidden',
                ...iconOnlyStyle,
            }}
            disabled={isDisabled}
            onClick={onClick}
            data-testid={testId}
            {...props}
        >
            {iconPosition === 'left' ? (
                <>
                    {iconNode}
                    {label && <span className="whitespace-nowrap">{label}</span>}
                </>
            ) : (
                <>
                    {label && <span className="whitespace-nowrap">{label}</span>}
                    {iconNode}
                </>
            )}
        </button>
    );
});

Button.displayName = 'Button';
